import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, CalendarPlus, CalendarRange, Maximize2, MoreHorizontal } from 'lucide-react';
import {
  addDays,
  allDayIso,
  byPosition,
  calendarDate,
  cardBar,
  fromDateInputs,
  daysBetween,
  dependencyConflicts,
  localDateKey,
  moveToDate,
  localTimeZone,
  startAfterDue,
  timeOf,
  type Bar,
  type BoardSnapshot,
  type CardSummary,
} from '@kaba/shared';
import { useUpdateCard } from '../../lib/board';
import { Button, cx, DropdownContent, DropdownItem, DropdownMenu, DropdownTrigger, LABEL_BG, Sheet } from '../ui/ui';
import { DateTimeField } from '../card/DateTimeField';
import { HolidayToggle, useHolidayDisplay } from '../HolidaySelect';

type Zoom = 'week' | 'month' | 'quarter';
const PX: Record<Zoom, number> = { week: 44, month: 20, quarter: 8 };
const ZOOM_LABEL: Record<Zoom, string> = { week: 'Wochen', month: 'Monate', quarter: 'Quartal' };
const ROW = 40;
const GROUP = 36;
const ZOOM_KEY = 'kaba.timeline.zoom';

type Mode = 'move' | 'start' | 'end';
interface Drag {
  cardId: string;
  mode: Mode;
  x0: number;
  delta: number;
}

const monthFmt = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' });
const dayFmt = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: 'numeric', month: 'short' });
const dayDate = (d: string) => new Date(`${d}T12:00:00`);

/** Neue Grenzen nach Ziehen/Tastatur */
function shifted(bar: Bar, mode: Mode, delta: number): Bar {
  if (mode === 'move') return { start: addDays(bar.start, delta), end: addDays(bar.end, delta) };
  if (mode === 'start') {
    const s = addDays(bar.start, delta);
    return { start: s <= bar.end ? s : bar.end, end: bar.end };
  }
  const e = addDays(bar.end, delta);
  return { start: bar.start, end: e >= bar.start ? e : bar.start };
}

/** Aus neuen Grenzen die Kartenänderung bilden (Uhrzeit einer Frist bleibt erhalten) */
export function patchFor(card: CardSummary, bar: Bar, next: Bar, mode: Mode) {
  const patch: { startAt?: string | null; startHasTime?: boolean; dueAt?: string | null; dueHasTime?: boolean; expectedVersion: number } = { expectedVersion: card.version };
  const hasStart = !!card.startAt;
  const hasDue = !!card.dueAt;
  // Ein Zeitraum über mehrere Tage braucht Start und Frist
  const span = next.start !== next.end && mode !== 'move';
  const needStart = hasStart || mode === 'start' || span;
  const needDue = hasDue || mode === 'end' || span;
  if (needStart && (next.start !== bar.start || !hasStart)) {
    // Uhrzeit eines Starts bleibt beim Verschieben erhalten
    patch.startAt = hasStart ? moveToDate(card.startAt, card.startHasTime, next.start) : allDayIso(next.start);
    if (!hasStart) patch.startHasTime = false;
  }
  if (needDue && (next.end !== bar.end || !hasDue)) {
    patch.dueAt = hasDue ? moveToDate(card.dueAt, card.dueHasTime, next.end) : allDayIso(next.end);
    if (!hasDue) patch.dueHasTime = false;
  }
  // Start und Frist am selben Tag, Start-Uhrzeit nach der Frist-Uhrzeit: Start wird ganztägig
  const s = patch.startAt !== undefined ? patch.startAt : card.startAt;
  const d = patch.dueAt !== undefined ? patch.dueAt : card.dueAt;
  const sTime = patch.startHasTime ?? card.startHasTime;
  if (s && sTime && startAfterDue(s, sTime, d, patch.dueHasTime ?? card.dueHasTime)) {
    patch.startAt = allDayIso(calendarDate(s, true));
    patch.startHasTime = false;
  }
  return patch;
}

export function TimelineView({
  snapshot,
  visible,
  canEdit,
  onOpenCard,
}: {
  snapshot: BoardSnapshot;
  visible: (c: CardSummary) => boolean;
  canEdit: boolean;
  onOpenCard: (id: string) => void;
}) {
  const update = useUpdateCard(snapshot.board.id);
  const [zoom, setZoom] = useState<Zoom>(() => {
    try {
      const z = localStorage.getItem(ZOOM_KEY) as Zoom | null;
      if (z && z in PX) return z;
    } catch {
      /* egal */
    }
    return window.matchMedia('(max-width: 639px)').matches ? 'month' : 'week';
  });
  const px = PX[zoom];
  const [collapsed, setCollapsed] = useState<string[]>([]);
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  dragRef.current = drag;
  const scroller = useRef<HTMLDivElement>(null);
  const today = localDateKey(new Date());

  const cards = useMemo(() => snapshot.cards.filter(visible), [snapshot.cards, visible]);
  const byId = useMemo(() => new Map(snapshot.cards.map((c) => [c.id, c])), [snapshot.cards]);
  const bars = useMemo(() => new Map(cards.map((c) => [c.id, cardBar(c)] as const)), [cards]);
  const conflicts = useMemo(() => dependencyConflicts(snapshot.cards), [snapshot.cards]);
  const conflictPairs = useMemo(() => new Set(conflicts.map((x) => `${x.blockerId}>${x.cardId}`)), [conflicts]);
  const labelColor = useMemo(() => new Map(snapshot.labels.map((l) => [l.id, l.color])), [snapshot.labels]);

  // Zeitraum: um heute und alle Balken herum
  const range = useMemo(() => {
    let min = addDays(today, -14);
    let max = addDays(today, 60);
    for (const b of bars.values()) {
      if (!b) continue;
      if (b.start < min) min = addDays(b.start, -7);
      if (b.end > max) max = addDays(b.end, 14);
    }
    // Wochenanfang (Montag)
    const dow = (dayDate(min).getDay() + 6) % 7;
    min = addDays(min, -dow);
    return { start: min, days: daysBetween(min, max) + 1 };
  }, [bars, today]);
  const x = (d: string) => daysBetween(range.start, d) * px;
  // 0.75: Feiertage ein-/ausblendbar (eigener Schalter, Bundesland wie im Kalender)
  const firstYear = Number(range.start.slice(0, 4));
  const lastYear = Number(addDays(range.start, range.days - 1).slice(0, 4));
  const hol = useHolidayDisplay('timelineHolidays', Array.from({ length: lastYear - firstYear + 1 }, (_, i) => firstYear + i));
  const holidayDays = useMemo(
    () => [...hol.holidays].filter(([d]) => d >= range.start && daysBetween(range.start, d) < range.days).sort(([a], [b]) => (a < b ? -1 : 1)),
    [hol.holidays, range],
  );

  const columns = useMemo(() => [...snapshot.columns].sort(byPosition), [snapshot.columns]);
  const groups = useMemo(
    () =>
      columns.map((col) => {
        const inCol = cards.filter((c) => c.columnId === col.id && bars.get(c.id));
        inCol.sort((a, b) => (bars.get(a.id)!.start < bars.get(b.id)!.start ? -1 : bars.get(a.id)!.start > bars.get(b.id)!.start ? 1 : a.position < b.position ? -1 : 1));
        return { col, cards: inCol };
      }),
    [columns, cards, bars],
  );
  const undated = cards.filter((c) => !bars.get(c.id));

  // Zeilen-Positionen für die Pfeile
  const rowY = new Map<string, number>();
  let y = 0;
  for (const g of groups) {
    if (!g.cards.length) continue;
    y += GROUP;
    if (collapsed.includes(g.col.id)) continue;
    for (const c of g.cards) {
      rowY.set(c.id, y + ROW / 2);
      y += ROW;
    }
  }
  const height = y;

  // Beim Öffnen zu „heute“ scrollen
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollLeft = Math.max(0, x(today) - el.clientWidth / 3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);

  const commit = (card: CardSummary, mode: Mode, delta: number) => {
    const bar = bars.get(card.id);
    if (!bar || !delta) return;
    const next = shifted(bar, mode, delta);
    const patch = patchFor(card, bar, next, mode);
    if (Object.keys(patch).length > 1) update.mutate({ id: card.id, patch });
  };

  // Nach einem echten Ziehen den folgenden Klick nicht als „öffnen“ werten
  const suppressClick = useRef(false);
  const suppressNextClick = () => {
    suppressClick.current = true;
    window.setTimeout(() => (suppressClick.current = false), 400);
  };
  const [datesFor, setDatesFor] = useState<CardSummary | null>(null);

  // Ziehen mit Maus/Stift (Pointer). Ohne Bearbeitungsrecht passiert hier nichts – der Klick öffnet.
  const startDrag = (e: React.PointerEvent, card: CardSummary, mode: Mode) => {
    if (!canEdit || e.button !== 0 || e.pointerType === 'touch') return;
    e.stopPropagation();
    const d = { cardId: card.id, mode, x0: e.clientX, delta: 0 };
    let moved = false;
    const cleanup = () => {
      document.body.classList.remove('select-none');
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onEsc);
      window.removeEventListener('blur', onCancel);
    };
    const onMove = (ev: PointerEvent) => {
      if (!moved && Math.abs(ev.clientX - d.x0) <= 3) return;
      if (!moved) {
        moved = true;
        setDrag(d);
        document.body.classList.add('select-none');
      }
      const delta = Math.round((ev.clientX - d.x0) / px);
      setDrag((cur) => (cur && cur.delta !== delta ? { ...cur, delta } : cur));
    };
    const onUp = (ev: PointerEvent) => {
      cleanup();
      setDrag(null);
      if (!moved) return; // normaler Klick → onClick öffnet die Karte
      suppressNextClick();
      commit(card, mode, Math.round((ev.clientX - d.x0) / px));
    };
    // Abbruch (Browser, Fenster verlassen, Escape): Vorschau verwerfen, nichts speichern
    const onCancel = () => {
      cleanup();
      setDrag(null);
      if (moved) suppressNextClick();
    };
    const onEsc = (ev: KeyboardEvent) => ev.key === 'Escape' && onCancel();
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onEsc);
    window.addEventListener('blur', onCancel);
  };

  // Touch: erst nach kurzem Halten ziehen (sonst scrollt die Ansicht); Halten ohne Bewegen öffnet die Termine
  const touch = useRef<{ card: CardSummary; mode: Mode; x0: number; y0: number; timer: number; active: boolean } | null>(null);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const onMove = (ev: TouchEvent) => {
      const t = touch.current;
      const p = ev.touches[0];
      if (!t || !p) return;
      if (!t.active) {
        if (Math.hypot(p.clientX - t.x0, p.clientY - t.y0) > 8) {
          clearTimeout(t.timer);
          touch.current = null;
        }
        return;
      }
      ev.preventDefault();
      const delta = Math.round((p.clientX - t.x0) / px);
      setDrag((cur) => (cur && cur.delta !== delta ? { ...cur, delta } : cur));
    };
    const onEnd = () => {
      const t = touch.current;
      touch.current = null;
      if (!t) return;
      clearTimeout(t.timer);
      if (!t.active) return; // kurzes Tippen → onClick öffnet die Karte
      const d = dragRef.current;
      setDrag(null);
      suppressNextClick();
      if (d && d.delta) commit(t.card, t.mode, d.delta);
      else setDatesFor(t.card);
    };
    // Vom System abgebrochene Geste: nichts speichern (N04)
    const onCancel = () => {
      const t = touch.current;
      touch.current = null;
      if (!t) return;
      clearTimeout(t.timer);
      if (t.active) suppressNextClick();
      setDrag(null);
    };
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onCancel);
    return () => {
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onCancel);
    };
  });
  const touchStart = (e: React.TouchEvent, card: CardSummary, mode: Mode) => {
    if (!canEdit) return;
    const p = e.touches[0];
    if (!p) return;
    const t = {
      card,
      mode,
      x0: p.clientX,
      y0: p.clientY,
      active: false,
      timer: window.setTimeout(() => {
        if (touch.current !== t) return;
        t.active = true;
        setDrag({ cardId: card.id, mode, x0: p.clientX, delta: 0 });
        navigator.vibrate?.(8);
      }, 350),
    };
    touch.current = t;
  };

  const onKey = (e: React.KeyboardEvent, card: CardSummary) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpenCard(card.id);
      return;
    }
    if (canEdit && (e.key === 't' || e.key === 'T')) {
      e.preventDefault();
      setDatesFor(card);
      return;
    }
    if (!canEdit || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
    e.preventDefault();
    const delta = e.key === 'ArrowRight' ? 1 : -1;
    commit(card, e.shiftKey ? 'end' : e.altKey ? 'start' : 'move', delta);
  };

  // Kopfzeile: Monate + Tage
  const months: { label: string; left: number; width: number }[] = [];
  for (let i = 0; i < range.days; ) {
    const d = addDays(range.start, i);
    const dt = dayDate(d);
    const daysInMonth = new Date(dt.getFullYear(), dt.getMonth() + 1, 0).getDate();
    const n = Math.min(daysInMonth - dt.getDate() + 1, range.days - i);
    months.push({ label: monthFmt.format(dt), left: i * px, width: n * px });
    i += n;
  }
  const width = range.days * px;
  const LEFT = 'w-36 sm:w-56';

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2">
        <div role="radiogroup" aria-label="Zoom" className="flex rounded-lg bg-black/5 p-0.5 dark:bg-white/10">
          {(Object.keys(PX) as Zoom[]).map((z) => (
            <button
              key={z}
              role="radio"
              aria-checked={zoom === z}
              onClick={() => {
                setZoom(z);
                try {
                  localStorage.setItem(ZOOM_KEY, z);
                } catch {
                  /* egal */
                }
              }}
              className={cx('min-h-9 rounded-md px-3 text-sm', zoom === z ? 'bg-[var(--panel)] font-semibold shadow-sm' : 'text-[var(--ink-soft)]')}
            >
              {ZOOM_LABEL[z]}
            </button>
          ))}
        </div>
        <button
          className="min-h-9 rounded-lg px-3 text-sm text-[var(--ink-soft)] hover:bg-black/5 dark:hover:bg-white/5"
          onClick={() => {
            const el = scroller.current;
            if (el) el.scrollTo({ left: Math.max(0, x(today) - el.clientWidth / 3), behavior: 'smooth' });
          }}
        >
          Heute
        </button>
        <HolidayToggle display={hol} />
        {hol.show && (
          <p className="sr-only" aria-live="polite">
            {holidayDays.length
              ? `Feiertage im Zeitraum: ${holidayDays.map(([d, name]) => `${name} (${dayDate(d).toLocaleDateString('de-DE', { day: 'numeric', month: 'numeric', year: 'numeric' })})`).join(', ')}`
              : 'Keine Feiertage im Zeitraum.'}
          </p>
        )}
        {conflicts.length > 0 && (
          <span className="rounded-lg bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-100">
            {conflicts.length === 1 ? '1 Terminkonflikt' : `${conflicts.length} Terminkonflikte`}
          </span>
        )}
        {canEdit && (
          <span className="hidden text-xs text-[var(--ink-soft)] lg:inline">
            Ziehen verschiebt · Ränder ändern Start/Frist · Menü ⋯, Rechtsklick, langes Halten oder T: Zeitraum bearbeiten · ←/→ verschiebt, mit Umschalt die Frist, mit Alt den Start
          </span>
        )}
      </div>

      <div ref={scroller} className="kaba-board-scroll relative flex-1 overflow-auto pb-24 sm:pb-6" role="grid" aria-label="Zeitleiste">
        <div className="relative" style={{ width: `calc(${width}px + 14rem)` }}>
          {/* Kopf */}
          <div className="sticky top-0 z-20 flex bg-[var(--surface)]">
            <div className={cx(LEFT, 'sticky left-0 z-10 shrink-0 border-b border-[var(--line)] bg-[var(--surface)]')} />
            <div className="relative h-12 border-b border-[var(--line)]" style={{ width }}>
              {months.map((m) => (
                <div key={m.left} className="absolute top-0 h-6 border-l border-[var(--line)] text-xs font-semibold leading-6" style={{ left: m.left, width: m.width }}>
                  {/* Monatsname bleibt beim Scrollen links sichtbar */}
                  <span className="sticky left-36 inline-block truncate px-1.5 sm:left-56">{m.label}</span>
                </div>
              ))}
              {Array.from({ length: range.days }, (_, i) => {
                const d = addDays(range.start, i);
                const dt = dayDate(d);
                const show = zoom === 'week' || (zoom === 'month' && dt.getDay() === 1) || (zoom === 'quarter' && dt.getDate() === 1);
                if (!show) return null;
                return (
                  <div
                    key={d}
                    className={cx(
                      'absolute top-6 h-6 text-center text-[11px] leading-6',
                      d === today ? 'font-bold text-accent-fg' : zoom === 'week' && hol.holidays.has(d) ? 'font-semibold text-amber-800 dark:text-amber-300' : 'text-[var(--ink-soft)]',
                    )}
                    style={{ left: i * px, width: zoom === 'week' ? px : 7 * px }}
                  >
                    {zoom === 'week' ? dt.getDate() : dt.getDate() + '.'}
                  </div>
                );
              })}
              {holidayDays.map(([d, name]) => (
                <div
                  key={`hol-${d}`}
                  aria-hidden
                  data-holiday={name}
                  title={`${name} (${dayDate(d).toLocaleDateString('de-DE', { day: 'numeric', month: 'numeric' })})`}
                  className="absolute bottom-0 h-1.5 rounded-t-sm bg-amber-500"
                  style={{ left: x(d), width: Math.max(px, 4) }}
                />
              ))}
            </div>
          </div>

          {/* Raster: Wochenenden und heute */}
          <div className="pointer-events-none absolute top-12 bottom-0 flex" aria-hidden>
            <div className={cx(LEFT, 'shrink-0')} />
            <div className="relative" style={{ width }}>
              {zoom !== 'quarter' &&
                Array.from({ length: range.days }, (_, i) => {
                  const d = addDays(range.start, i);
                  const wd = dayDate(d).getDay();
                  return wd === 0 || wd === 6 ? <div key={d} className="absolute top-0 bottom-0 bg-black/[0.025] dark:bg-white/[0.03]" style={{ left: i * px, width: px }} /> : null;
                })}
              {holidayDays.map(([d]) => (
                <div key={`holg-${d}`} className="absolute top-0 bottom-0 bg-amber-500/[0.10] dark:bg-amber-400/[0.10]" style={{ left: x(d), width: Math.max(px, 2) }} />
              ))}
              <div className="absolute top-0 bottom-0 w-0.5 bg-accent/70" style={{ left: x(today) + px / 2 }} />
            </div>
          </div>

          {/* Zeilen */}
          <div className="relative">
            {groups.map(({ col, cards: rows }) => {
              if (!rows.length) return null;
              const closed = collapsed.includes(col.id);
              return (
                <div key={col.id} role="rowgroup">
                  {/* Gruppenzeile mit Rolle „row“ – sonst meldet axe fehlende Kinder des Rasters */}
                  <div role="row" className="flex" style={{ height: GROUP }}>
                    <div role="rowheader" className="contents">
                    <button
                      className={cx(LEFT, 'sticky left-0 z-10 flex shrink-0 items-center gap-1.5 bg-[var(--surface)] px-3 text-left text-sm font-semibold')}
                      aria-expanded={!closed}
                      onClick={() => setCollapsed((c) => (closed ? c.filter((x) => x !== col.id) : [...c, col.id]))}
                    >
                      {closed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                      <span className="truncate">{col.title}</span>
                      <span className="text-xs font-normal text-[var(--ink-soft)]">{rows.length}</span>
                    </button>
                    </div>
                  </div>
                  {!closed &&
                    rows.map((c) => {
                      const bar0 = bars.get(c.id)!;
                      const dragging = drag?.cardId === c.id;
                      const bar = dragging ? shifted(bar0, drag.mode, drag.delta) : bar0;
                      const conflict = conflicts.some((k) => k.cardId === c.id);
                      const color = c.labelIds.map((id) => labelColor.get(id)).find(Boolean);
                      const w = (daysBetween(bar.start, bar.end) + 1) * px;
                      const label = `${c.title}: ${dayFmt.format(dayDate(bar.start))}${bar.end !== bar.start ? ` bis ${dayFmt.format(dayDate(bar.end))}` : ''}${conflict ? ', Terminkonflikt' : ''}`;
                      return (
                        <div key={c.id} role="row" className="flex" style={{ height: ROW }}>
                          <div role="rowheader" className={cx(LEFT, 'sticky left-0 z-10 flex shrink-0 items-center bg-[var(--surface)] pl-7')}>
                            <button
                              onClick={() => onOpenCard(c.id)}
                              className={cx('min-w-0 flex-1 truncate text-left text-sm hover:underline', c.completedAt && 'text-[var(--ink-soft)] line-through')}
                            >
                              {c.title}
                            </button>
                            <RowMenu card={c} canEdit={canEdit} onOpen={() => onOpenCard(c.id)} onDates={() => setDatesFor(c)} />
                          </div>
                          <div className="relative" style={{ width }} role="gridcell">
                            <div
                              role="button"
                              tabIndex={0}
                              aria-label={label}
                              title={label}
                              data-card={c.id}
                              data-card-id={c.id}
                              onPointerDown={(e) => startDrag(e, c, 'move')}
                              onTouchStart={(e) => touchStart(e, c, 'move')}
                              onClick={() => {
                                if (suppressClick.current) return;
                                onOpenCard(c.id);
                              }}
                              onKeyDown={(e) => onKey(e, c)}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                if (canEdit && !matchMedia('(pointer: coarse)').matches) setDatesFor(c);
                              }}
                              className={cx(
                                'group absolute top-1 flex h-8 select-none items-center rounded-md text-xs font-medium shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1',
                                color ? cx(LABEL_BG[color], 'text-white') : 'bg-accent text-on-accent',
                                c.completedAt && 'opacity-50',
                                conflict && 'ring-2 ring-amber-400',
                                dragging && 'z-10 shadow-lg ring-2 ring-accent/60',
                                canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
                              )}
                              style={{ left: x(bar.start), width: Math.max(w, 12) }}
                            >
                              {/* Ränder: Trefferfläche größer als sichtbar (halb außerhalb); bei sehr schmalen Balken nur über „Termine“ */}
                              {canEdit && w >= 24 && (
                                <span
                                  aria-hidden
                                  data-handle="start"
                                  className="absolute inset-y-0 -left-2.5 z-10 w-5 cursor-ew-resize rounded-l-md hover:bg-white/30 pointer-coarse:-left-4 pointer-coarse:w-7"
                                  onPointerDown={(e) => startDrag(e, c, 'start')}
                                  onTouchStart={(e) => {
                                    e.stopPropagation();
                                    touchStart(e, c, 'start');
                                  }}
                                />
                              )}
                              <span className="pointer-events-none truncate px-2">{w > 60 ? (c.completedAt ? '✓ ' : '') + c.title : ''}</span>
                              {canEdit && w >= 24 && (
                                <span
                                  aria-hidden
                                  data-handle="end"
                                  className="absolute inset-y-0 -right-2.5 z-10 w-5 cursor-ew-resize rounded-r-md hover:bg-white/30 pointer-coarse:-right-4 pointer-coarse:w-7"
                                  onPointerDown={(e) => startDrag(e, c, 'end')}
                                  onTouchStart={(e) => {
                                    e.stopPropagation();
                                    touchStart(e, c, 'end');
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              );
            })}

            {/* Abhängigkeiten als Pfeile */}
            <svg className="pointer-events-none absolute top-0" style={{ left: 0, width: `calc(${width}px + 14rem)`, height }} aria-hidden>
              <defs>
                <marker id="kaba-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
                  <path d="M0,0 L8,4 L0,8 z" fill="var(--ink-soft)" />
                </marker>
                <marker id="kaba-arrow-warn" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
                  <path d="M0,0 L8,4 L0,8 z" fill="#d97706" />
                </marker>
              </defs>
              <TimelineArrows
                cards={cards}
                byId={byId}
                bars={bars}
                rowY={rowY}
                drag={drag}
                x={(d) => x(d)}
                px={px}
                offsetRef={scroller}
                conflictPairs={conflictPairs}
              />
            </svg>
          </div>

          {!groups.some((g) => g.cards.length) && (
            <p className="px-4 py-8 text-sm text-[var(--ink-soft)]">Noch keine Karte mit Start oder Frist. Termine trägst du in den Kartendetails ein.</p>
          )}
        </div>

        {undated.length > 0 && (
          <details className="sticky left-0 mx-4 mt-6 max-w-xl rounded-xl border border-[var(--line)] bg-[var(--panel)]">
            <summary className="flex min-h-11 cursor-pointer items-center gap-2 px-3 text-sm font-medium">
              <CalendarPlus size={16} /> Ohne Termin ({undated.length})
            </summary>
            <ul className="divide-y divide-[var(--line)] border-t border-[var(--line)]">
              {undated.map((c) => (
                <li key={c.id} className="flex items-center pr-1">
                  <button onClick={() => onOpenCard(c.id)} className="flex min-h-11 min-w-0 flex-1 items-center px-3 text-left text-sm hover:bg-black/5 dark:hover:bg-white/5">
                    <span className="truncate">{c.title}</span>
                  </button>
                  {canEdit && (
                    <Button size="sm" variant="ghost" onClick={() => setDatesFor(c)} aria-label={`Zeitraum für „${c.title}“ festlegen`}>
                      <CalendarRange size={15} /> <span className="hidden sm:inline">Zeitraum festlegen</span>
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
      {datesFor && <DatesSheet card={byId.get(datesFor.id) ?? datesFor} onClose={() => setDatesFor(null)} onSave={(patch) => update.mutate({ id: datesFor.id, patch })} />}
    </div>
  );
}

/** Zeile: Karte öffnen oder Zeitraum bearbeiten – sichtbarer Weg ohne Ziehen, Rechtsklick oder Halten */
function RowMenu({ card, canEdit, onOpen, onDates }: { card: CardSummary; canEdit: boolean; onOpen: () => void; onDates: () => void }) {
  return (
    <DropdownMenu>
      <DropdownTrigger asChild>
        <button
          type="button"
          aria-label={`Aktionen für „${card.title}“`}
          className="inline-flex size-9 shrink-0 items-center justify-center rounded-md text-[var(--ink-soft)] hover:bg-black/5 dark:hover:bg-white/10"
        >
          <MoreHorizontal size={16} />
        </button>
      </DropdownTrigger>
      <DropdownContent align="start">
        {canEdit && (
          <DropdownItem icon={<CalendarRange size={16} />} onSelect={onDates}>
            Zeitraum bearbeiten
          </DropdownItem>
        )}
        <DropdownItem icon={<Maximize2 size={16} />} onSelect={onOpen}>
          Karte öffnen
        </DropdownItem>
      </DropdownContent>
    </DropdownMenu>
  );
}

/** Zeitraum (Start, Frist) direkt eingeben – Alternative zum Ziehen (Touch, kleine Balken, Tastatur) */
function DatesSheet({
  card,
  onClose,
  onSave,
}: {
  card: CardSummary;
  onClose: () => void;
  onSave: (patch: { startAt: string | null; startHasTime: boolean; dueAt: string | null; dueHasTime: boolean; expectedVersion: number }) => void;
}) {
  const [start, setStart] = useState({ date: card.startAt ? calendarDate(card.startAt, card.startHasTime) : '', time: card.startAt && card.startHasTime ? timeOf(card.startAt) : null });
  const [due, setDue] = useState({ date: card.dueAt ? calendarDate(card.dueAt, card.dueHasTime) : '', time: card.dueAt && card.dueHasTime ? timeOf(card.dueAt) : null });
  const startIso = fromDateInputs(start.date, start.time);
  const dueIso = fromDateInputs(due.date, due.time);
  const invalid = startAfterDue(startIso, !!start.time, dueIso, !!due.time);
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} title="Zeitraum bearbeiten" side="center">
      <form
        className="flex flex-col gap-3 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (invalid) return;
          onSave({
            startAt: startIso,
            startHasTime: !!startIso && !!start.time,
            dueAt: dueIso,
            dueHasTime: !!dueIso && !!due.time,
            expectedVersion: card.version,
          });
          onClose();
        }}
      >
        <p className="truncate text-sm font-medium">{card.title}</p>
        <DateTimeField
          label="Start"
          date={start.date}
          time={start.time}
          error={invalid ? 'Der Start liegt nach der Frist.' : null}
          onCommit={(date, time) => {
            setStart({ date, time });
            return null;
          }}
        />
        <DateTimeField
          label="Fällig"
          date={due.date}
          time={due.time}
          onCommit={(date, time) => {
            setDue({ date, time });
            return null;
          }}
        />
        <p className="text-xs text-[var(--ink-soft)]">Zeiten in {localTimeZone()} · 24-Stunden-Format</p>
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={invalid}>
            Speichern
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

function TimelineArrows({
  cards,
  byId,
  bars,
  rowY,
  drag,
  x,
  px,
  offsetRef,
  conflictPairs,
}: {
  cards: CardSummary[];
  byId: Map<string, CardSummary>;
  bars: Map<string, Bar | null>;
  rowY: Map<string, number>;
  drag: Drag | null;
  x: (d: string) => number;
  px: number;
  offsetRef: React.RefObject<HTMLDivElement | null>;
  conflictPairs: Set<string>;
}) {
  // Linke Titelspalte: Breite aus dem DOM (responsive)
  const [left, setLeft] = useState(224);
  useEffect(() => {
    const el = offsetRef.current?.querySelector('[role="rowheader"]') as HTMLElement | null;
    if (el) setLeft(el.offsetWidth);
  });
  const barOf = (id: string) => {
    const b = bars.get(id);
    if (!b) return null;
    return drag?.cardId === id ? shifted(b, drag.mode, drag.delta) : b;
  };
  const paths: React.ReactNode[] = [];
  for (const c of cards) {
    for (const dep of c.dependsOn) {
      const from = byId.get(dep);
      if (!from || from.completedAt) continue;
      const a = barOf(dep);
      const b = barOf(c.id);
      const ya = rowY.get(dep);
      const yb = rowY.get(c.id);
      if (!a || !b || ya === undefined || yb === undefined) continue;
      const x1 = left + x(a.end) + px;
      const x2 = left + x(b.start);
      const warn = conflictPairs.has(`${dep}>${c.id}`);
      const mid = Math.max(x1 + 8, Math.min(x2 - 8, x1 + 12));
      const d = x2 > x1 + 16 ? `M${x1},${ya} H${mid} V${yb} H${x2 - 2}` : `M${x1},${ya} h8 V${(ya + yb) / 2} H${x2 - 10} V${yb} H${x2 - 2}`;
      paths.push(
        <path
          key={`${dep}>${c.id}`}
          d={d}
          fill="none"
          stroke={warn ? '#d97706' : 'var(--ink-soft)'}
          strokeWidth={warn ? 2 : 1.25}
          strokeDasharray={warn ? undefined : '4 3'}
          markerEnd={`url(#${warn ? 'kaba-arrow-warn' : 'kaba-arrow'})`}
          opacity={0.9}
        />,
      );
    }
  }
  return <>{paths}</>;
}
